import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';

import { metabaseUrl } from '../constants/widgetBuilderLinks.js';
import SettingToggle from './SettingToggle.jsx';

import { ExternalLink } from 'lucide-react';

/**
 * `SETTINGS` — access settings and the escape hatch (CT-5197).
 *
 * The new prototype also shows a refresh-rate control here (`Every 15
 * minutes`). It is **deliberately not built**: decision 7 of the 2026-09-04
 * session removed it — Thomas, verbatim: _"este tema de Refresh, omítanlo"_ —
 * and the 2026-09-08 session never revisited it. Confirmed with Alex on
 * 2026-09-09 and raised on CT-5197 for Thomas to settle.
 */
function WidgetSettingsSection({ endUserFiltersEnabled, onEndUserFiltersChange }) {
  const { t } = useTranslation('dashboards');

  const handleToggle = useCallback(
    () => onEndUserFiltersChange(!endUserFiltersEnabled),
    [endUserFiltersEnabled, onEndUserFiltersChange],
  );

  const metabase = metabaseUrl();

  return (
    <div className="flex flex-col gap-3">
      <SettingToggle
        checked={endUserFiltersEnabled}
        label={t('widgetBuilder.configure.endUserFilters')}
        description={t('widgetBuilder.configure.endUserFiltersHelper')}
        onChange={handleToggle}
      />

      {/*
        Rendered only when the workspace has a Metabase configured — see
        `widgetBuilderLinks.js` for why there is no fallback URL.

        A real `<a>`, not `Button`: the DS `Button` always renders a `<button>`
        and takes no `href`, so the alternative would be an anchor wrapping a
        button. Styled from tokens to match a tertiary button.
      */}
      {metabase ? (
        <a
          href={metabase}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 self-start rounded-md text-xs font-medium text-brand-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-brand-400"
        >
          {t('widgetBuilder.configure.metabase')}
          <ExternalLink size={14} aria-hidden />
        </a>
      ) : null}
    </div>
  );
}

WidgetSettingsSection.propTypes = {
  endUserFiltersEnabled: PropTypes.bool,
  onEndUserFiltersChange: PropTypes.func.isRequired,
};

export default WidgetSettingsSection;
